import { useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import './ScratchCard.css';
import {
  COLORS,
  IMAGE_BASE_URL,
  SCRATCH_COVER_IMAGE,
  SCRATCH_COVER_LINES,
  SCRATCH_CTA,
  getPrizeImageUrl,
  normalizeAssetUrl,
} from '../brand';

const isLosingPrize = (prize) => {
  if (!prize?.prize_type) return false;
  const t = prize.prize_type.toLowerCase();
  return t.includes('try_again') || t.includes('reessayer') || t.includes('perdu');
};

const CARD_W = 336;  // ratio 1,40 = celui du visuel officiel (15x21 cm)
const CARD_H = 240;

// Visuel de la carte : chargé une seule fois et partagé entre toutes les cartes,
// pour qu'il soit déjà prêt quand la première carte s'affiche.
const COVER_SRC = `${IMAGE_BASE_URL}/${encodeURIComponent(SCRATCH_COVER_IMAGE)}`;
let coverImage = null;
let coverPromise = null;
export const preloadScratchCover = () => {
  if (!coverPromise) {
    coverPromise = new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { coverImage = img; resolve(img); };
      img.onerror = reject;
      img.src = COVER_SRC;
    });
  }
  return coverPromise;
};

// Dessin de secours (même design, tracé à la main) : affiché tant que le visuel
// n'est pas chargé, ou définitivement s'il ne charge pas.
const drawFallbackCover = (ctx) => {
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Rappel des lots en haut (configuré dans brand.js)
  ctx.fillStyle = COLORS.primary;
  ctx.font = 'bold 9px Arial';
  ctx.textAlign = 'center';
  SCRATCH_COVER_LINES.forEach((line, i) => {
    ctx.fillText(line, CARD_W / 2, 14 + i * 12);
  });

  // Zone rouge en bas (vague)
  ctx.fillStyle = COLORS.primary;
  ctx.beginPath();
  ctx.moveTo(0, CARD_H * 0.70);
  ctx.bezierCurveTo(CARD_W * 0.3, CARD_H * 0.62, CARD_W * 0.7, CARD_H * 0.76, CARD_W, CARD_H * 0.68);
  ctx.lineTo(CARD_W, CARD_H);
  ctx.lineTo(0, CARD_H);
  ctx.closePath();
  ctx.fill();

  // "GRATTER ICI !" en blanc gras
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 22px "Arial Black", Arial';
  ctx.textAlign = 'center';
  ctx.fillText(SCRATCH_CTA, CARD_W / 2, CARD_H * 0.88);
};

const ScratchCard = ({ prize, onReveal, isRevealed }) => {
  const canvasRef = useRef(null);
  const [isScratching, setIsScratching] = useState(false);
  const [scratchPercentage, setScratchPercentage] = useState(0);
  const [autoRevealed, setAutoRevealed] = useState(false);

  const imageUrl = getPrizeImageUrl(prize?.prize_type, prize?.prize_label, prize?.name);
  const iconUrl = normalizeAssetUrl(prize?.icon);
  const isLosing = isLosingPrize(prize);

  // Dessiner l'habillage de la carte comme surface à gratter
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // Visuel déjà en cache : on le dessine tout de suite, aucun délai
    if (coverImage) {
      ctx.drawImage(coverImage, 0, 0, CARD_W, CARD_H);
      return;
    }

    // Sinon : design de secours immédiatement, puis le visuel dès qu'il arrive
    drawFallbackCover(ctx);
    let cancelled = false;
    preloadScratchCover()
      .then((img) => {
        // Ne pas repeindre si le joueur a déjà commencé à gratter
        if (!cancelled && scratchPercentage === 0) {
          ctx.drawImage(img, 0, 0, CARD_W, CARD_H);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculateScratchPercentage = (ctx, w, h) => {
    const data = ctx.getImageData(0, 0, w, h).data;
    let transparent = 0;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 128) transparent++;
    }
    return (transparent / (data.length / 4)) * 100;
  };

  const getPos = (canvas, e) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const scratch = (x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 28, 0, Math.PI * 2);
    ctx.fill();

    const pct = calculateScratchPercentage(ctx, canvas.width, canvas.height);
    setScratchPercentage(pct);

    if (pct > 50 && !autoRevealed) {
      setAutoRevealed(true);
      const ctx2 = canvas.getContext('2d');
      ctx2.clearRect(0, 0, canvas.width, canvas.height);
      setScratchPercentage(100);
      if (!isLosing) {
        setTimeout(() => {
          confetti({
            particleCount: 180,
            spread: 90,
            origin: { y: 0.55 },
            colors: [COLORS.primary, '#ffffff', 'var(--brand-primary)', 'var(--brand-primary)', 'var(--brand-primary)'],
            startVelocity: 45,
            gravity: 0.8,
            scalar: 1.1,
          });
        }, 300);
      }
      if (onReveal && !isRevealed) setTimeout(() => onReveal(), 300);
    }
  };

  const handleStart = (e) => {
    if (isRevealed) return;
    e.preventDefault();
    setIsScratching(true);
    const pos = getPos(canvasRef.current, e);
    scratch(pos.x, pos.y);
  };

  const handleMove = (e) => {
    if (!isScratching || isRevealed) return;
    e.preventDefault();
    const pos = getPos(canvasRef.current, e);
    scratch(pos.x, pos.y);
  };

  const handleEnd = () => setIsScratching(false);

  return (
    <div className="scratch-card-container">
      <div className="scratch-card">

        {/* Contenu révélé sous le design */}
        <div className={`scratch-card-content ${isLosing ? 'losing' : 'winning'}`}>
          {isLosing ? (
            <div className="lose-content">
              <div className="lose-icon">😢</div>
              <p className="lose-text">Pas de chance !</p>
              <p className="lose-sub">Retentez lors d'un prochain achat</p>
            </div>
          ) : (
            prize && (
              <>
                <div className="prize-img-wrap">
                  {imageUrl ? (
                    <img src={imageUrl} alt={prize.prize_label || 'Lot'} className="prize-img" />
                  ) : prize.image_url ? (
                    <img src={prize.image_url} alt={prize.prize_label || 'Lot'} className="prize-img" />
                  ) : iconUrl ? (
                    <img src={iconUrl} alt={prize.prize_label || 'Lot'} className="prize-img" />
                  ) : (
                    <span style={{ fontSize: 48 }}>🎁</span>
                  )}
                </div>
                <div className="prize-details">
                  <p className="prize-name-text">{prize.prize_label || prize.name}</p>
                  {prize.prize_code && (
                    <div className="prize-code-box">CODE : {prize.prize_code}</div>
                  )}
                </div>
              </>
            )
          )}
        </div>

        {/* Canvas — surface à gratter (design carte.jpg) */}
        {!isRevealed && (
          <canvas
            ref={canvasRef}
            width={CARD_W}
            height={CARD_H}
            className={`scratch-surface${isScratching ? ' scratching' : ''}`}
            onMouseDown={handleStart}
            onMouseMove={handleMove}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
            onTouchStart={handleStart}
            onTouchMove={handleMove}
            onTouchEnd={handleEnd}
          />
        )}
      </div>

      {!isRevealed && scratchPercentage > 0 && scratchPercentage < 50 && (
        <div className="scratch-hint">Continuez à gratter...</div>
      )}
    </div>
  );
};

export default ScratchCard;
